import { Box, Stack, useMobileBreakpoint } from '@customink/pigment-react';
import type { ProjectItem } from '@customink/project-service-react-components';
import { useCallback } from 'react';

import { GroupItemFittedProductTip } from './GroupItemFittedProductTip';
import { GroupItemHeader } from './GroupItemHeader';
import type { ProjectItemProductData } from '../EditGroupSizesForm/useGroupSizesForm';

import { ProductAvailability } from '@/components/ProjectItem/ProductAvailability';
import { ProjectItemEditSizesFields } from '@/components/ProjectItem/ProjectItemEditSizesFields';
import { ProjectItemProof } from '@/components/ProjectItem/ProjectItemProof';
import { useSizingGuide } from '@/hooks/useSizingGuide';

export interface GroupItemProps {
  productItemData: ProjectItemProductData;
  item: ProjectItem;
  itemIndex: number;
  onDelete: (itemId: string) => Promise<void>;
  groupItems: ProjectItem[];
}

export function GroupItem({
  groupItems,
  productItemData,
  item,
  itemIndex,
  onDelete,
}: GroupItemProps) {
  const isMobile = useMobileBreakpoint();
  const isDesktop = !isMobile;
  const { openSizingGuide } = useSizingGuide(productItemData.productId);

  const handleDeleteProjectItem = async () => {
    if (item?.id) {
      await onDelete(item.id);
    }
  };

  const colorName = productItemData.color?.name;
  const getFieldName = useCallback(
    (size: string) => `items.${itemIndex}.sizes.${size}`,
    [itemIndex],
  );

  return (
    <Box
      aria-label={`${productItemData.name} in ${colorName}`}
      data-testid="group-item"
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      gap="0.75rem"
      role="listitem"
      width="100%"
    >
      {isDesktop && (
        <Box sx={{ width: '7.58rem' }}>
          <ProjectItemProof item={item} />
        </Box>
      )}
      <Stack flex={1} gap="0.5rem">
        <GroupItemHeader
          groupItems={groupItems}
          isMobile={isMobile}
          item={item}
          onDelete={handleDeleteProjectItem}
          onViewSizingGuide={openSizingGuide}
          productItemData={productItemData}
        />
        <Stack gap="0.25rem">
          {productItemData.fitted && <GroupItemFittedProductTip />}
          <ProjectItemEditSizesFields getFieldName={getFieldName} item={item} />
          <ProductAvailability colorId={item?.product?.colorId} />
        </Stack>
      </Stack>
    </Box>
  );
}
